"use client";

import PlusCircleIcon from "@heroicons/react/24/outline/PlusCircleIcon";
import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useRef, useCallback, useEffect } from "react";
import { useSession } from "next-auth/react";
import { CONTACT_PHONE_NUMBER } from "@/lib/constants";
import S3Image from "@/components/S3Image";
import { WHATSAPP_TAGS } from "@/@core/enums/whatsapp_tags.enum";
import useWhatsappContext from "@/hooks/useWhatsappContext";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import useGetWpHref from "@/hooks/useGetWpHref";
import { useVenueCompanies } from "@/hooks/use-companies";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/translationContext";

const CompanyModal = dynamic(() => import("../all-companies/modals/offer-modal"));
const CompanyActionCheckerModal = dynamic(
  () => import("../modals/company/ActionChecker"),
);

interface Props {
  venueTitle: string;
  venueId: number;
  initialData?: any;
}

export default function CompaniesInfiniteScroll({
  venueTitle,
  venueId,
  initialData,
}: Props) {
  const { t } = useTranslation();
  const { status } = useSession();
  const whatsappTags = useWhatsappContext();
  const getWhatsappHrefUrl = useGetWpHref();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [openDiscountModal, setOpenDiscountModal] = useState(false);
  const [activeItem, setActiveItem]: any = useState({});

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useVenueCompanies(venueId);

  const addCompanyWpContext = whatsappTags.getContext({
    venue: venueTitle ?? "",
  });

  // Flatten all pages data
  const allCompanies = data?.pages?.flatMap(page => page.data) || [];
  const usageData = data?.pages?.[0]?.usageData;
  const totalCount = initialData?.summary?.count || allCompanies.length;

  // Scroll navigation functions
  const scrollLeft = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -200,
        behavior: "smooth",
      });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 200,
        behavior: "smooth",
      });
    }
  }, []);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollRef.current || !hasNextPage || isFetchingNextPage) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;

    // Load more when 80% scrolled
    if (scrollPercentage > 0.8) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Attach scroll listener
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-red-500">Şirketler yüklenirken bir hata oluştu.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Navigation Buttons */}
      <div className="absolute -left-4 top-1/2 -translate-y-1/2 z-10">
        <Button
          variant="outline"
          size="icon"
          onClick={scrollLeft}
          className="h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-50"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="absolute -right-4 top-1/2 -translate-y-1/2 z-10">
        <Button
          variant="outline"
          size="icon"
          onClick={scrollRight}
          className="h-8 w-8 rounded-full bg-white shadow-md hover:bg-gray-50"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <div className="flex gap-2 items-center pb-2">
          {/* Modal Components */}
          {openDiscountModal && status === "authenticated" ? (
            <CompanyModal
              usageData={usageData}
              title={activeItem.title}
              venue={{
                title: venueTitle,
                id: venueId,
              }}
              discount={activeItem.discount}
              venueCompanyDescription={activeItem.venueCompanyDescription}
              shortDescription={activeItem.shortDescription}
              id={activeItem.id}
              logo={activeItem.logo}
              isOpen={openDiscountModal}
              onClose={() => setOpenDiscountModal(false)}
            />
          ) : (
            openDiscountModal && (
              <CompanyActionCheckerModal
                isOpen={openDiscountModal}
                onClose={() => setOpenDiscountModal(false)}
              />
            )
          )}

          {/* Company Items */}
          {allCompanies.map((item: any) => (
            <div
              onClick={() => {
                setActiveItem(item);
                setOpenDiscountModal(true);
              }}
              key={item.id}
              className="p-1 !w-[64px] font-['DM Sans'] gap-1 cursor-pointer items-center flex flex-col flex-shrink-0"
            >
              <div className="!w-[56px] !h-[52px]">
                <S3Image
                  width={56}
                  height={52}
                  src={`company/${item.id}/logo/${item.logo}`}
                  alt={item.title + " Logosu"}
                  sizes="56px"
                  className="!w-14 h-[52px] cursor-pointer rounded-[19.2px] shadow-lg"
                />
              </div>

              <h3 className="text-center font-medium text-xs text-primary w-full text-ellipsis line-clamp-2 text-wrap h-8">
                {item.title}
              </h3>

              <p className="font-medium text-xs text-assistant">
                %{item.discount}
              </p>
            </div>
          ))}

          {/* Loading Skeletons */}
          {(isLoading || isFetchingNextPage) &&
            Array.from(Array(15).keys()).map((index: number) => (
              <div
                key={`skeleton-${index}`}
                className="p-1 !h-[100px] !w-[64px] flex flex-col gap-2 items-center flex-shrink-0"
              >
                <Skeleton className="w-[56px] rounded-[19.2px] h-[52px]"></Skeleton>
                <Skeleton className="w-[42px] h-[16px] rounded"></Skeleton>
                <Skeleton className="w-5 h-[16px] rounded"></Skeleton>
              </div>
            ))}

          {/* Add Company Button */}
          <WhatsappLinkWithBeacon
            title={t("company-discount-add-company-button")}
            href={getWhatsappHrefUrl({
              tag: WHATSAPP_TAGS.ADD_COMPANY_IN_VENUE_DETAIL,
              phone: CONTACT_PHONE_NUMBER,
              translateValues: addCompanyWpContext,
            })}
          >
            <div className="p-1 font-['DM Sans'] gap-1 items-center flex flex-col flex-shrink-0">
              <PlusCircleIcon className="size-6 text-assistant text-2xl" />
              <span className="font-medium text-center text-xs text-assistant">
                Şirketini Ekle
              </span>
            </div>
          </WhatsappLinkWithBeacon>
        </div>
      </div>
    </div>
  );
}